<template>
  <v-form ref="formRef" class="lighting-form method-form" @submit.prevent="">
    <!-- Betriebszeit -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebszeit</span>
      </div>

      <!-- Manual Uptime -->
      <v-checkbox
        v-model="form.useManualUptime"
        :error-messages="validationErrors?.use_manual_uptime"
        label="Manuelle Brenndauer"
        :disabled="!sharedData.building_type"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.use_manual_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.use_manual_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.use_manual_uptime"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('use_manual_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Uptime -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Brenndauer"
        suffix="h/a"
        :disabled="!sharedData.building_type || !form.useManualUptime"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            title="Referenzwert übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Lichtanlage -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-lightbulb-on-outline</v-icon>
        <span>Lichtanlage</span>
      </div>

      <!-- Age reference only -->
      <v-autocomplete
        v-model="form.age"
        :error-messages="validationErrors?.age"
        label="Alter der Lichtanlage"
        :items="ages"
        item-title="age"
        item-value="id"
        :disabled="!form.uptime || !sharedData.bgf"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.age" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.age }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'
import api from '@/plugins/axios'

/* Props from parent */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object,  default: () => ({}) },
  sharedData: { type: Object,  default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Pinia (for generator lists / loading state) */

/* Fields mapping (frontend -> backend) */
const fields = {
  actionId:        'action_id',
  useManualUptime: 'use_manual_uptime',
  uptime:          'uptime',
  age:             'age',
}

/* Composable integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: { useManualUptime: false },
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false), // Lighting1 is not a shared form
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Local data sources */
const buildingTypes = ref([])
const ages = ref([])

/* Derived shared inputs */
const buildingType = computed(() => props.sharedData?.building_type)

/* Watchers */
watch(buildingType, () => {
  if (!buildingType.value) return
  const bt = buildingTypes.value.find(x => x.id === buildingType.value)
  if (bt) form.uptime = bt.norm_uptime
  form.useManualUptime = !!form.useManualUptime
}, { immediate: true })

/* API loaders (modernized paths) */
async function fetchBuildingTypes() {
  try {
    const { data } = await api.get('/methods/lighting1/building_type/', {
      params: { planned: props.planned || null, reference: props.reference || null },
    })
    buildingTypes.value = data
  } catch {
    // Optional: add toast or error banner
  }
}

async function fetchAges() {
  try {
    const { data } = await api.get('/methods/lighting1/age/', {
      params: { planned: props.planned || null, reference: props.reference || null },
    })
    ages.value = data

    // Planned: preselect first age entry (matches legacy behavior)
    if (props.planned && Array.isArray(ages.value) && ages.value.length > 0) {
      form.age = ages.value[0].id
    }
  } catch {
    // Optional: add toast or error banner
  }
}

/* Expose validate method for parent to trigger on save error */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}
defineExpose({ validateForm })

/* Lifecycle */
onMounted(async () => {
  await fetchBuildingTypes()
  await fetchAges()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.lighting-form {
  width: 100%;
}

/* Field styling */
.lighting-form :deep(.v-field) {
  border-radius: 18px;
}

.lighting-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkboxes */
.lighting-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested/dependent fields */
.lighting-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint/copy icons */
.lighting-form :deep(.v-input__append),
.lighting-form :deep(.v-field__append-inner) {
  align-items: center;
}

.lighting-form :deep(.v-input__append .v-icon),
.lighting-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.lighting-form :deep(.v-input__append .v-btn),
.lighting-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.lighting-form :deep(.v-input__append .v-btn:hover),
.lighting-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled fields */
.lighting-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .lighting-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
